<template>
  <a
    class="d-book-list-entry loadable"
    :class="{ 'expandable': isExpandable,
              'highlight': book.isHighlighted,
              'loading': book.isLoading,
              'skipped': book.isSkipped,
              'with-nav': withNav }"
    :href="(withNav ? book.url : null)"
    @click="toggleExpanded"
  >
    <DuiLoadingIndicator
      v-if="book.isLoading"
      class="middle"
      size="lg"
    />

    <div class="loaded-content">
      <div
        v-if="book.isSkipped"
        class="grid-flex grid-middle"
      >
        <div class="grid-item">
          <span>You skipped <i>{{ book.title }}</i></span>
        </div>
        <div class="grid-item-shrink right-actions">
          <button
            class="button-white-bordered button-full-sm"
            type="button"
            @click.stop.prevent="undoSkipBook()"
          >
            Undo
          </button>
        </div>
      </div>

      <div v-if="!book.isSkipped">
        <!-- Book info -->
        <div class="book-info">
          <!-- Book cover -->
          <div class="cover">
            <a
              class="dui-book-cover-link"
              :href="book.url"
              :title="book.title"
              @click.stop
            >
              <DuiBookCover :book-cover-url="book.cover.medium.url" />
            </a>
          </div>
          <!-- END Book cover -->

          <div
            v-if="$slots['status-content']"
            class="status-content"
          >
            <slot name="status-content" />
          </div>

          <!-- Book title -->
          <div class="book-title">
            <a
              :href="book.url"
              :title="book.title"
              @click.stop
            >
              <h3>{{ book.title }}</h3>
            </a>
          </div>
          <!-- END Book title -->

          <!-- Author and Genre -->
          <div
            v-if="(showAuthor || showGenre)"
            class="author-and-genre"
          >
            <DUserLinkPopup
              v-if="showAuthor"
              :user-id="book.author_id"
            >
              <template slot="content">
                <p
                  class="author"
                >
                  {{ book.author }}
                </p>
              </template>
            </DUserLinkPopup>


            <DuiReviewRating
              v-if="(rating !== null)"
              :rating="rating"
              :video-review="videoReview"
            />

            <span
              v-if="showGenre"
              class="label label-sm genre"
            >
              {{ book.genre }}
            </span>
          </div>
          <!-- END Author and Genre -->

          <div
            v-if="$slots['top-content']"
            class="top-content"
          >
            <slot name="top-content" />
          </div>

          <div
            v-if="$slots['center-content']"
            class="center-content"
          >
            <slot name="center-content" />
          </div>

          <div
            v-if="$slots['bottom-content']"
            class="bottom-content"
          >
            <slot name="bottom-content" />
          </div>
        </div>
        <!-- END Book info -->

        <div
          v-if="$slots['footer-content']"
          class="footer-content"
        >
          <slot name="footer-content" />
        </div>
      </div>
    </div>
  </a>
</template>

<script>
import DUserLinkPopup from '@d-components/d-user-link-popup/d-user-link-popup.vue';

export default {
  components: {
    DUserLinkPopup,
  },
  props: {
    book: {
      type: Object,
      required: true,
    },
    isExpandable: {
      type: Boolean,
      default: false,
    },
    rating: {
      type: Number,
      default: null,
    },
    showAuthor: {
      type: Boolean,
      default: false,
    },
    showGenre: {
      type: Boolean,
      default: false,
    },
    videoReview: {
      type: Boolean,
      default: false,
    },
    withNav: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    toggleExpanded(e) {
      if (!this.isExpandable) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      this.$set(this.book, 'isExpanded', !this.book.isExpanded);
      this.$emit('click');
    },
    undoSkipBook() {
      this.$emit('undo-skip-book', this.book);
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.d-book-list-entry {
  color: getColor($black);
  cursor: default;
  display: block;
  font-weight: normal;
  padding: 0 $_2em $_3em;
  margin: 0 (-$_2em);

  &:hover {
    color: getColor($black);
  }

  @include screenLessThan(sm) {
    padding: 0 $_1em $_3em;
    margin: 0 (-$_1em);
  }

  @include transition((
    background-color
  ));

  &.expandable:not(.skipped):hover,
  &.with-nav:hover {
    background-color: getColor($grey, base);
    cursor: pointer;
  }

  &.highlight {
    animation: highlight-list-entry 2s;
  }

  &.skipped {
    background-color: getColor($grey);
    border-radius: $border-radius-base;
    padding-bottom: $_2em;
    padding-top: $_2em;
  }

  .book-info {
    border-top: 2px solid $color-translucent-black;
    display: grid;
    grid-column-gap: $_2em;
    grid-template-columns: $book-cover-width-xs auto;
    grid-template-rows: auto auto auto 1fr;
    padding-top: $_3em;

    @include screenMoreThan(sm) {
      grid-template-columns: $book-cover-width-sm auto;
    }

    .dui-book-cover-link {
      display: flex;
    }
  }

  .book-title h3 {
    color: getColor($black);
    display: inline-block;

    @include screenLessThan(md) {
      font-size: $font-size-book-title-sm;
    }
  }

  .cover {
    grid-row: span 4;

    .dui-book-cover {
      height: $book-cover-height-xs;
      width: $book-cover-width-xs;

      &.square {
        height: $book-cover-width-xs;
      }

      @include screenMoreThan(sm) {
        height: $book-cover-height-sm;
        width: $book-cover-width-sm;

        &.square {
          height: $book-cover-width-sm;
        }
      }
    }
  }

  .author-and-genre {
    margin-bottom: $_1em;

    p.author {
      display: inline-block;
      margin: 0 $_1em 0 0;
      vertical-align: middle;
    }

    .label.genre {
      margin-bottom: $_1em;
      vertical-align: middle;
    }

    .dui-review-rating .video-icon {
      margin-right: $_1em;
    }
  }

  .status-content {
    flex: 0 1 auto;
    margin-bottom: $_1em;

    .status-left {
      margin: 0 0 $_1em 0;

      @include screenMoreThan(sm) {
        margin: 0 $_1em 0 0;
      }
    }

    .status-right {
      align-items: center;
      display: flex;

      > * {
        margin-left: $_0-5em;

        &:first-child {
          margin-left: 0;
        }
      }
    }
  }

  .center-content {
    position: relative;
  }

  .top-content,
  .center-content,
  .bottom-content {
    flex: 1;
  }

  .center-content,
  .bottom-content,
  .footer-content {
    @include screenLessThan(md) {
      grid-column: span 2;
      margin-top: $_2em;
    }

    p:last-child {
      margin-bottom: 0;
    }
  }

  @include screenLessThan(sm) {
    .right-actions {
      margin-top: $_2em;
    }
  }

  .bottom-content {
    align-self: end;
  }

  .footer-content {
    margin-top: $_2em;
  }
}
</style>
